import { getIDToken } from "utils/misc";
import { Question } from "./types/common";
import { Book, QuestionGroup } from "./types/persisted";

const BASE_URL = `${process.env.REACT_APP_API_URL_V2}/v2/support/health-forms`;

const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  const token = getIDToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

type QuestionListItem = {
  id: number;
  question: string;
  responseType: string;
  semanticKey?: string;
};

type BookListItem = {
  id: number;
  name: string;
  type: string;
};

type QuestionGroupListItem = {
  id: number;
  name: string;
  type: string;
};

export const healthFormsApi = {
  questions: {
    list: async (): Promise<QuestionListItem[]> => {
      const res = await fetch(`${BASE_URL}/questions`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch questions");
      return res.json();
    },

    get: async (id: number): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question ${id}`);
      return res.json();
    },

    create: async (question: Omit<Question, "id" | "hash" | "updatedAt">): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(question),
      });
      if (!res.ok) throw new Error("Failed to create question");
      return res.json();
    },

    update: async (id: number, question: Question): Promise<Question> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(question),
      });
      if (!res.ok) throw new Error(`Failed to update question ${id}`);
      if (res.status === 204) return question;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/questions/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question ${id}`);
    },
  },

  books: {
    list: async (): Promise<BookListItem[]> => {
      const res = await fetch(`${BASE_URL}/books`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch books");
      return res.json();
    },

    get: async (id: number): Promise<Book> => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch book ${id}`);
      return res.json();
    },

    create: async (book: Omit<Book, "id" | "updatedAt">): Promise<Book> => {
      const res = await fetch(`${BASE_URL}/books`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(book),
      });
      if (!res.ok) throw new Error("Failed to create book");
      return res.json();
    },

    update: async (id: number, book: Book): Promise<Book> => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(book),
      });
      if (!res.ok) throw new Error(`Failed to update book ${id}`);
      if (res.status === 204) return book;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/books/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete book ${id}`);
    },
  },

  questionGroups: {
    list: async (): Promise<QuestionGroupListItem[]> => {
      const res = await fetch(`${BASE_URL}/question-groups`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch question groups");
      return res.json();
    },

    get: async (id: number): Promise<QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to fetch question group ${id}`);
      return res.json();
    },

    create: async (group: Omit<QuestionGroup, "id" | "updatedAt">): Promise<QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question-groups`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(group),
      });
      if (!res.ok) throw new Error("Failed to create question group");
      return res.json();
    },

    update: async (id: number, group: QuestionGroup): Promise<QuestionGroup> => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify(group),
      });
      if (!res.ok) throw new Error(`Failed to update question group ${id}`);
      if (res.status === 204) return group;
      return res.json();
    },

    delete: async (id: number): Promise<void> => {
      const res = await fetch(`${BASE_URL}/question-groups/${id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error(`Failed to delete question group ${id}`);
    },
  },
};

export type { QuestionListItem, BookListItem, QuestionGroupListItem };
